'use client'

import Image from 'next/image'
import { useEffect, useState } from 'react'
import { API_BASE_URL } from '@/config/api'

interface ImageComponentProps {
  url?: string;
  img_title?: string;
  object_cover?: boolean;
  object_contain?: boolean;
  imgPriority?: boolean;
  width?: number;
  height?: number;
  /** Next/Image quality 1–100. Default 75. Must be listed in next.config images.qualities. */
  quality?: number;
  /**
   * Optional lower quality for viewports ≤767px. Emits a `<picture>` `<source>`
   * with `/_next/image&q=…` so mobile LCP can use e.g. 60 while desktop keeps
   * `quality` (e.g. 75). Same asset URL unless `mobile_url` is also set.
   */
  mobileQuality?: number;
  /** Width-full; height follows image aspect ratio (uses width/height only as ratio hints for Next/Image). */
  fullWidthNaturalHeight?: boolean;
  /** Render at the file's intrinsic width/height (uses width/height props when both set, else detects on load). */
  naturalSize?: boolean;
  /** When true, image scales up slightly on hover (wrapper uses overflow hidden). */
  hoverScale?: boolean;
  /** When true with `hoverScale`, uses `group-hover:` so a parent with `group` triggers the scale. */
  groupHover?: boolean;
  /** Appended to the underlying image `className` (e.g. `object-center`). */
  className?: string;
  /**
   * Responsive sizes hint, e.g. `"(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"`.
   * When omitted, a smart default is computed from the layout flags:
   *  - `fullWidthNaturalHeight` → `"100vw"`
   *  - `naturalSize`            → `"<intrinsicWidth>px"`
   *  - everything else (cards/avatars) → conservative
   *    `"(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"`.
   * Passing a tighter custom value is the single biggest PageSpeed win for
   * non-hero images — Next can then pick a much smaller `srcset` candidate.
   */
  sizes?: string;
  /**
   * Optional mobile art direction: wraps the main image in `<picture>` with
   * `source media="(max-width: 767px)"` so the correct asset is chosen on
   * first paint (no JS breakpoint — avoids desktop image flashing on narrow
   * viewports). The mobile `srcset` is built via `_next/image` so the asset
   * also benefits from AVIF/WebP transcoding + responsive width selection,
   * matching the desktop `<img>` pipeline.
   */
  mobile_url?: string | null;
  /**
   * Skip the Next.js image optimizer entirely — serves the raw asset from the
   * source URL with no resizing, no WebP/AVIF transcoding, no `srcset`. Use
   * sparingly; the only legitimate reason is when an asset must be byte-exact
   * (e.g. designer-supplied JPEG with intentional banding/grain, or a one-off
   * banner where re-encoding visibly degrades the source). Bypasses both the
   * desktop `<img>` and the mobile `<source>` branches.
   *
   * Trade-off: full-size CMS asset is sent to every viewport. Don't enable
   * this on multi-MB images unless you've verified the bandwidth cost.
   */
  unoptimized?: boolean;
}

/** Same rules as internal image URL handling (public paths + remote URLs). */
export function sanitizeImageUrl(url?: string) {
  if (!url || url?.trim() === '') return '/images/home/nav_logo_laptop.png';

  const trimmed = url.trim();

  if (trimmed.startsWith('/') || trimmed.startsWith('./') || trimmed.startsWith('../')) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    return parsed.href;
  } catch {
    // Relative CMS paths e.g. "media/123-file.png" — prefix API origin
    const mediaOrigin = API_BASE_URL.replace(/\/api\/?$/, '');
    return `${mediaOrigin}/${trimmed.replace(/^\/+/, '')}`;
  }
}

export default function ImageComponent({
  url,
  img_title,
  object_cover = true,
  object_contain = false,
  imgPriority = false,
  width,
  height,
  quality = 75,
  mobileQuality,
  fullWidthNaturalHeight = false,
  naturalSize = false,
  hoverScale = false,
  groupHover = false,
  className: imageClassName,
  sizes,
  mobile_url,
  unoptimized = false,
}: ImageComponentProps) {
  const safeUrl = sanitizeImageUrl(url);
  const mobileTrimmed = mobile_url?.trim();
  const safeMobile =
    mobileTrimmed && mobileTrimmed.length > 0
      ? sanitizeImageUrl(mobileTrimmed)
      : null;
  const resolvedMobileQuality =
    mobileQuality != null && mobileQuality !== quality ? mobileQuality : null;

  const hasExplicitSize = Boolean(width && height);

  const [intrinsic, setIntrinsic] = useState<{ w: number; h: number } | null>(
    hasExplicitSize ? { w: width!, h: height! } : null
  );

  useEffect(() => {
    if (!naturalSize || hasExplicitSize) return;

    let cancelled = false;
    const probe = new window.Image();
    probe.onload = () => {
      if (
        !cancelled &&
        probe.naturalWidth > 0 &&
        probe.naturalHeight > 0
      ) {
        setIntrinsic({
          w: probe.naturalWidth,
          h: probe.naturalHeight,
        });
      }
    };
    probe.src = safeUrl;

    return () => {
      cancelled = true;
    };
  }, [naturalSize, safeUrl, hasExplicitSize]);

  if (naturalSize) {
    if (!intrinsic) {
      return (
        <span
          className="inline-block min-h-4 min-w-4"
          aria-hidden
        />
      );
    }

    const objectFitClass = object_contain ? 'object-contain' : '';

    const naturalImage = (
      <Image
        src={safeUrl}
        width={intrinsic.w}
        height={intrinsic.h}
        quality={quality}
        alt={img_title || 'title not found'}
        className={`size-full max-w-none ${objectFitClass} ${imageClassName ?? ''}`.trim()}
        sizes={sizes ?? `${intrinsic.w}px`}
        priority={imgPriority}
        loading={imgPriority ? 'eager' : 'lazy'}
        unoptimized={unoptimized}
        draggable={false}
        onContextMenu={(e) => e.preventDefault()}
        fetchPriority={imgPriority ? 'high' : 'auto'}
      />
    );

    return (
      <span
        className="inline-block shrink-0 leading-none"
        style={{
          width: intrinsic.w,
          height: intrinsic.h,
          minWidth: intrinsic.w,
          minHeight: intrinsic.h,
        }}
      >
        {naturalImage}
      </span>
    );
  }

  const w = width ?? (fullWidthNaturalHeight ? 1920 : 1000);
  const h = height ?? (fullWidthNaturalHeight ? 1080 : 1000);

  const objectFitClass = object_contain ? 'object-contain' : object_cover ? 'object-cover' : '';

  const sizeClass = fullWidthNaturalHeight ? 'w-full h-auto max-w-full' : 'w-full h-full';

  const hoverClass = hoverScale
    ? groupHover
      ? 'transition-transform duration-300 ease-out group-hover:scale-[1.02]'
      : 'transition-transform duration-300 ease-out hover:scale-[1.02]'
    : '';

  /**
   * Smart default for `sizes` when the consumer hasn't supplied one. Always
   * passing a `sizes` (instead of letting Next default to `100vw`) lets the
   * optimizer pick the smallest `srcset` candidate for the slot — biggest
   * single PageSpeed win for non-hero images.
   *
   * Note: the `naturalSize` arm here is defensive — that branch returns
   * earlier above, so this ternary only fires for the main / `fullWidth…`
   * paths in practice.
   */
  // Prefer slot-aware defaults over 100vw so /_next/image doesn't pick
  // viewport-wide candidates for cards/columns (PageSpeed "oversized images").
  const computedSizes =
    sizes ??
    (fullWidthNaturalHeight
      ? '(min-width: 1280px) 33vw, (min-width: 768px) 50vw, 100vw'
      : naturalSize
        ? `${intrinsic?.w ?? w}px`
        : '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw');

  const image = (
    <Image
      src={safeUrl}
      width={w}
      height={h}
      quality={quality}
      alt={img_title || 'title not found'}
      className={`${sizeClass} ${objectFitClass} ${hoverClass} ${imageClassName ?? ''}`.trim()}
      style={fullWidthNaturalHeight ? { width: '100%', height: 'auto' } : undefined}
      sizes={computedSizes}
      priority={imgPriority}
      loading={imgPriority ? 'eager' : 'lazy'}
      unoptimized={unoptimized}
      draggable={false}
      onContextMenu={(e) => e.preventDefault()}
      fetchPriority={imgPriority ? 'high' : 'auto'}
    />
  );

  /**
   * Build a multi-width `srcset` for the mobile `<source>` that goes through
   * `/_next/image`, so mobile users get WebP + correctly-sized variants
   * just like the desktop `<img>` does. Without this the mobile branch ships
   * the raw CMS asset (full-size PNG/JPG, no modern format) — which was the
   * single biggest LCP regression for mobile traffic.
   *
   * Widths are aligned with the lower bands of `deviceSizes` in
   * `next.config.ts`. The browser still picks one candidate based on the
   * `<source sizes>` hint below.
   */
  const buildOptimizedSrcSet = (originalUrl: string, q: number) =>
    [360, 384, 512, 640, 700, 750, 828]
      .map(
        (candidateWidth) =>
          `/_next/image?url=${encodeURIComponent(originalUrl)}&w=${candidateWidth}&q=${q} ${candidateWidth}w`
      )
      .join(', ');

  /** Aligns with `useIsMobile(768)` (`innerWidth < 768`). */
  const pictureClass = fullWidthNaturalHeight
    ? 'block w-full max-w-full'
    : 'block h-full w-full';

  // Art-direction URL and/or a lower mobile quality both need <picture>.
  const mobileSourceUrl = safeMobile ?? (resolvedMobileQuality != null ? safeUrl : null);
  const mobileSourceQuality = resolvedMobileQuality ?? quality;

  const pictureWrapped =
    mobileSourceUrl != null && !unoptimized ? (
      <picture className={pictureClass}>
        <source
          media="(max-width: 767px)"
          srcSet={buildOptimizedSrcSet(mobileSourceUrl, mobileSourceQuality)}
          sizes={computedSizes}
        />
        {image}
      </picture>
    ) : safeMobile != null && unoptimized ? (
      <picture className={pictureClass}>
        <source
          media="(max-width: 767px)"
          srcSet={safeMobile}
          sizes={computedSizes}
        />
        {image}
      </picture>
    ) : (
      image
    );

  if (!hoverScale) return pictureWrapped;

  return (
    <div
      className={
        fullWidthNaturalHeight ? 'w-full overflow-hidden' : 'h-full w-full overflow-hidden'
      }
    >
      {pictureWrapped}
    </div>
  );
}
